import React, { useState } from "react";
import { Star, Quote, Send, CheckCircle, User, Phone, Target } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrustSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    goal: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const testimonials = [
    {
      id: 1,
      name: "Rajesh Kumar",
      location: "Mumbai",
      text: "Started with just ₹5,000 monthly SIP 8 years ago. Today my corpus is ₹12 Lakhs. Fortune Finserv guided me through every market cycle.",
      rating: 5,
      avatar: "RK",
    },
    {
      id: 2,
      name: "Priya Sharma",
      location: "Delhi",
      text: "The team helped me understand that insurance is not an expense but protection. Their advice on term insurance saved my family during tough times.",
      rating: 5,
      avatar: "PS",
    },
    {
      id: 3,
      name: "Amit Patel",
      location: "Ahmedabad",
      text: "I was a classic 'market timer' wasting years waiting for dips. Fortune Finserv's SIP approach changed my perspective. Now I'm on track for my retirement.",
      rating: 5,
      avatar: "AP",
    },
  ];

  const goals = [
    { value: "retirement", label: "Retirement Planning" },
    { value: "child-education", label: "Child's Education" },
    { value: "wealth-creation", label: "Wealth Creation" },
    { value: "tax-saving", label: "Tax Saving" },
    { value: "life-insurance", label: "Life Insurance" },
    { value: "health-insurance", label: "Health Insurance" },
    { value: "other", label: "Other" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.goal) {
      toast.error("Please fill all fields");
      return;
    }

    // Basic phone validation
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error("Please enter a valid 10-digit Indian mobile number");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/leads`, formData);
      setIsSubmitted(true);
      toast.success(response.data.message || "Thank you! We'll contact you soon.");
      setFormData({ name: "", phone: "", goal: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 sm:py-32 footer-dark"
      data-testid="trust-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left: Lead Form */}
          <div>
            <span className="inline-block text-sm uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-4">
              Start Your Journey
            </span>
            <h2
              className="text-3xl sm:text-4xl tracking-tight font-semibold leading-snug text-white mb-4"
              data-testid="contact-title"
            >
              Let's Build Your Wealth Together
            </h2>
            <p className="text-emerald-200 mb-8">
              Fill the form below and our certified financial advisor will contact you
              within 24 hours with a personalized plan.
            </p>

            {isSubmitted ? (
              <div
                className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-8 text-center"
                data-testid="form-success-message"
              >
                <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Thank You!
                </h3>
                <p className="text-emerald-200">
                  Our team will reach out to you within 24 hours.
                </p>
                <Button
                  variant="outline"
                  className="mt-6 border-emerald-500 text-emerald-400 hover:bg-emerald-500/20"
                  onClick={() => setIsSubmitted(false)}
                  data-testid="submit-another-btn"
                >
                  Submit Another Query
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-6"
                data-testid="lead-form"
              >
                {/* Name Input */}
                <div>
                  <Label htmlFor="name" className="text-emerald-200 mb-2 block">
                    Your Name
                  </Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-xl h-14 focus:border-emerald-500 focus:ring-emerald-500"
                      data-testid="input-name"
                    />
                  </div>
                </div>

                {/* Phone Input */}
                <div>
                  <Label htmlFor="phone" className="text-emerald-200 mb-2 block">
                    Phone Number
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="Enter 10-digit mobile number"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-xl h-14 focus:border-emerald-500 focus:ring-emerald-500"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                {/* Goal Select */}
                <div>
                  <Label htmlFor="goal" className="text-emerald-200 mb-2 block">
                    Your Financial Goal
                  </Label>
                  <div className="relative">
                    <Target className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10 pointer-events-none" />
                    <Select
                      value={formData.goal}
                      onValueChange={(value) =>
                        setFormData({ ...formData, goal: value })
                      }
                    >
                      <SelectTrigger
                        className="pl-12 bg-white/10 border-white/20 text-white rounded-xl h-14 focus:border-emerald-500 focus:ring-emerald-500 [&>span]:text-left"
                        data-testid="select-goal"
                      >
                        <SelectValue placeholder="Select your goal" />
                      </SelectTrigger>
                      <SelectContent data-testid="goal-options">
                        {goals.map((goal) => (
                          <SelectItem
                            key={goal.value}
                            value={goal.value}
                            data-testid={`goal-option-${goal.value}`}
                          >
                            {goal.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full h-14 text-lg font-medium shadow-lg shadow-emerald-500/25 btn-shine"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    "Submitting..."
                  ) : (
                    <>
                      Start My Wealth Journey
                      <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-400 text-center">
                  By submitting, you agree to our privacy policy. We never share your
                  data.
                </p>
              </form>
            )}
          </div>

          {/* Right: Testimonials */}
          <div>
            <span className="inline-block text-sm uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-4">
              Client Stories
            </span>
            <h3 className="text-2xl font-semibold text-white mb-8">
              What Our Clients Say
            </h3>

            <div className="space-y-6">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="testimonial-card bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
                  data-testid={`testimonial-${testimonial.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-emerald-400 font-semibold">
                        {testimonial.avatar}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-white">
                          {testimonial.name}
                        </h4>
                        <span className="text-xs text-slate-400">
                          • {testimonial.location}
                        </span>
                      </div>
                      <div className="flex gap-1 mb-3">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star
                            key={i}
                            className="w-4 h-4 fill-amber-400 text-amber-400"
                          />
                        ))}
                      </div>
                      <p className="text-emerald-100 text-sm leading-relaxed">
                        "{testimonial.text}"
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-xs text-slate-400 mb-4">TRUSTED BY</p>
              <div className="flex flex-wrap gap-4 items-center">
                <div className="bg-white/10 rounded-lg px-4 py-2 text-xs text-white">
                  AMFI Registered
                </div>
                <div className="bg-white/10 rounded-lg px-4 py-2 text-xs text-white">
                  IRDAI Licensed
                </div>
                <div className="bg-white/10 rounded-lg px-4 py-2 text-xs text-white">
                  5+ Years
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
